import {MxCell} from '../../editor-interface/shared/models/mx-cell.interface';
import {
  IAttributeJson,
  IFlowlineJson,
  IResourceJson
} from '../../diagram-data/diagram-json-handler.service';
import {FlowlineForm} from './flowline-form.enum';
import {ServerStyleObject} from './styles.model';
import {stickerCSSClassMapper} from '../../shared/constants/color-picker.constants';
import {DiagramTypes as DiagramReportTypes} from '@q9elements/ui-kit/common';

export enum CanvasElementType {
  ACTIVITY = 'Activity',
  FLOWLINE = 'Flowline',
  STICKY_NOTE = 'StickyNote',
  IMAGE = 'Image',
  TEXT = 'Text'
}

type CanvasElementForDbClick = CanvasElementType.ACTIVITY | CanvasElementType.STICKY_NOTE;

export interface DiagramGeneralSettings {
  canAddToLegend: boolean;
  canAutoGenerateDrilldown: boolean;
  canAddAttribute: boolean;
  canGenerateStoriesAI: boolean;
  canGenerateRequirements: boolean;
  addSFAHeader: boolean;
  zoomToFitNeeded: boolean;
  isLabelMovable: boolean;

  /**
   * String or translation key for the tag name in the header.
   */
  tagName?: string;
  tagColor: string;

  allowedDrilldownSubtypes: string[];

  canvasElementForDbClick?: CanvasElementForDbClick;
  disableShortcutsForDbClick?: boolean;
  disabledCanvasElements?: CanvasElementType[];
  disableVersioning?: boolean;
  disableChangesTab?: boolean;
  disableThemeTab?: boolean;
  disableResources?: boolean;
  disableSubtypeChange?: boolean;

  /**
   * List of report types that are enabled for the diagram.
   * If not provided, all report types are enabled.
   */
  enabledReportTypes?: DiagramReportTypes[];
}

export interface ActivitySettings {
  text: string;
  position: {x: number; y: number; w: number; h: number};
  dragHandlePosition: {x: number; y: number; w: number; h: number};
  footerText: string;
  canPlaceFrontBack: boolean;
  canAddFooterText: boolean;
  canMakeContainerCard: boolean;
  selectedTabIndex: number;
  defaultActivityShape: {w: number; h: number};
  calcAddHeight: (cell: MxCell) => number;
  createActivityWithFlowline: boolean;
  template: string;
  defaultResources?: IResourceJson[];
  defaultAttributes?: IAttributeJson[];
  attributePadding?: boolean;
  customStyle?: boolean;
  style?: Partial<ServerStyleObject>;
}

export interface FlowlineConfig {
  text: string;
  srcType: string;
  tgtType: string;
  waypoints: {x: number; y: number}[];
  srcPosition: {x: number; y: number};
  tgtPosition: {x: number; y: number};
  position: {
    connectionPoints: {entryX: number; entryY: number} | {exitX: number; exitY: number};
    offset: {x: number; y: number};
    x: number;
    y: number;
  };
  srcId: number;
  tgtId: number;
}

export interface FlowlineSettings {
  dynamicLabel: string;
  canAddStartText: boolean;
  canAddEndText: boolean;
  canAddInteraction: boolean;
  defaultFlowlineShape: {w: number; h: number};
  config: FlowlineConfig | null;
  geo: (data?: IFlowlineJson) => mxGeometry;
  template: string;
  form: FlowlineForm;
  defaultGeoY?: number;
}

export interface ResourceSettings {
  size: number;
  height: number;
  resourcePlacement: boolean;
}

export interface RichTextCreatePayload {
  number: number;
  text: string;
  position: {x: number; y: number; w: number; h: number};
  link?: string;
  customStyle?: boolean;
  style?: ServerStyleObject;
  locked?: boolean;
}

export enum PositionType {
  CENTER = 'center',
  DEFAULT = 'default'
}

export interface RichTextPayloadParams {
  x: number;
  y: number;
  number: number;
  style?: ServerStyleObject;
  position?: PositionType;
}

export interface RichTextSettings {
  template: string;
  defaultTextShape: {w: number; h: number};
  preparePayload: (params: RichTextPayloadParams) => RichTextCreatePayload;
}

export interface StickerNotePayloadParams {
  x: number;
  y: number;
  mxKey: number;
  position?: PositionType;
  color?: string | keyof typeof stickerCSSClassMapper;
}

export interface StickerNoteCreatePayload {
  mxKey: number;
  text: string;
  position: {x: number; y: number; w: number; h: number};
  paperClass: (typeof stickerCSSClassMapper)[keyof typeof stickerCSSClassMapper];
}

export interface StickerNoteSettings {
  defaultShape: {w: number; h: number};
  preparePayload: (params: StickerNotePayloadParams) => StickerNoteCreatePayload;
}

export interface ActivityType {
  type?: string;
  tooltip: string;
  img?: string;
  icon?: string;
  settings: (x?: number, y?: number) => ActivitySettings;
}

// Minimal mxPoint interface for mxGraph usage
export interface mxPoint {
  x: number;
  y: number;
}

// Minimal mxGeometry interface for mxGraph usage
export interface mxGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
  relative?: boolean;
  sourcePoint?: mxPoint;
  targetPoint?: mxPoint;
  points?: mxPoint[];
  offset?: mxPoint;
  alternateBounds?: mxGeometry;
  clone?(): mxGeometry;
  equals?(other: mxGeometry): boolean;
  setTerminalPoint?(point: mxPoint, isSource: boolean): void;
  // Add more properties/methods as needed based on further usage
}

export interface ResourceActivityPayload {
  type: string;
  text: string;
  footerText: string;
  tooltip?: string;
  style?: Partial<ServerStyleObject>;
}
